import { localizationService } from 'src/services';

export function removeProhibitedAriaLabelsFromGrid(containerElement: HTMLElement | Document = document): void {
	const spans = containerElement.querySelectorAll<HTMLSpanElement>(
		'.k-grid th .k-link span[aria-label="Sortable"], .k-grid th .k-icon[aria-label="Sortable"]'
	);

	spans.forEach(span => {
		if (!span.hasAttribute('role') || span.getAttribute('role') === '') {
			span.removeAttribute('aria-label');
		}
	});
}

export function ensureIframeTitles(
	defaultTitle: string = 'Content Frame',
	containerElement: HTMLElement | Document = document
): void {
	const iframes = containerElement.querySelectorAll<HTMLIFrameElement>('iframe:not([title])');

	iframes.forEach(iframe => {
		const hasAriaLabel = iframe.hasAttribute('aria-label') && iframe.getAttribute('aria-label')?.trim() !== '';
		const hasAriaLabelledby =
			iframe.hasAttribute('aria-labelledby') && iframe.getAttribute('aria-labelledby')?.trim() !== '';

		if (!hasAriaLabel && !hasAriaLabelledby) {
			let title = defaultTitle;
			if (iframe.classList.contains('k-iframe')) {
				title = localizationService.getLocalizedString('screen.labels.screenReader.editorContentFrame');
			} else if (iframe.id) {
				title = `${iframe.id} frame`;
			}
			iframe.setAttribute('title', title);
		}
	});
}

export function makeScrollableRegionsFocusable(containerElement: HTMLElement | Document = document): void {
	const scrollableElements = containerElement.querySelectorAll<HTMLElement>(
		'.table-responsive, .k-grid table, .k-grid-content, .k-virtual-scrollable-wrap'
	);

	scrollableElements.forEach(element => {
		const computedStyle = window.getComputedStyle(element);
		const hasScroll =
			computedStyle.overflow === 'auto' ||
			computedStyle.overflow === 'scroll' ||
			computedStyle.overflowX === 'auto' ||
			computedStyle.overflowX === 'scroll' ||
			computedStyle.overflowY === 'auto' ||
			computedStyle.overflowY === 'scroll';

		const isScrollable = hasScroll || element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

		if (isScrollable) {
			const hasFocusableContent =
				element.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])') !== null;

			if (!hasFocusableContent && !element.hasAttribute('tabindex')) {
				element.setAttribute('tabindex', '0');
				if (!element.hasAttribute('aria-label') && !element.hasAttribute('aria-labelledby')) {
					if (element.tagName === 'TABLE' || element.classList.contains('k-grid-content')) {
						element.setAttribute('aria-label', localizationService.getLocalizedString('screen.labels.screenReader.scrollableDataTable'));
					}
				}
			}
		}
	});
}

export function applyKendoGridAccessibilityFixes(containerElement: HTMLElement | Document = document): void {
	removeProhibitedAriaLabelsFromGrid(containerElement);
	makeScrollableRegionsFocusable(containerElement);
}

export function applyAllAccessibilityFixes(containerElement: HTMLElement | Document = document): void {
	removeProhibitedAriaLabelsFromGrid(containerElement);
	ensureIframeTitles(localizationService.getLocalizedString('screen.labels.screenReader.contentFrame'), containerElement);
	makeScrollableRegionsFocusable(containerElement);
}

export function patchKendoGridAria(containerElement: HTMLElement | Document = document): void {
	const spans = containerElement.querySelectorAll<HTMLElement>('.k-link span[aria-label]');
	spans.forEach(span => {
		if (!span.getAttribute('role') && !span.closest('th')) {
			span.setAttribute('role', 'img');
		}
	});
	const gridDivs = containerElement.querySelectorAll<HTMLElement>('div.k-grid[aria-colcount], div.k-grid[aria-rowcount]');
	gridDivs.forEach(div => {
		if (!div.getAttribute('role')) {
			div.removeAttribute('aria-colcount');
			div.removeAttribute('aria-rowcount');
		}
	});
	const selectAllLabel = localizationService.getLocalizedString('grid.selectAll');
	const headerCheckboxes = containerElement.querySelectorAll<HTMLElement>('.k-grid-header .k-checkbox:not([aria-label])');
	headerCheckboxes.forEach(checkbox => {
		checkbox.setAttribute('aria-label', selectAllLabel);
	});
	const tables = containerElement.querySelectorAll<HTMLElement>('.k-grid table');
	tables.forEach(table => {
		const isScrollable = table.scrollWidth > table.clientWidth || table.scrollHeight > table.clientHeight;
		if (isScrollable && !table.getAttribute('tabindex')) {
			table.setAttribute('tabindex', '0');
			if (!table.getAttribute('aria-label') && !table.getAttribute('aria-labelledby')) {
				table.setAttribute('aria-label', localizationService.getLocalizedString('screen.labels.screenReader.scrollableDataTable'));
			}
		}
	});
	const dropdownWraps = containerElement.querySelectorAll<HTMLElement>('.k-dropdown-wrap[role="listbox"]');
	dropdownWraps.forEach(span => {
		span.removeAttribute('role');
		span.removeAttribute('aria-haspopup');
		span.removeAttribute('aria-expanded');
		span.removeAttribute('aria-owns');
	});
}
